import React from 'react';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { CoreBumpRecommendationListingCard } from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { formatBid } from './bump_helpers';
import { RCAlertBox, RCIcon } from '@reverbdotcom/cadence/components';
import { withPrefix } from '@reverbdotcom/commons/src/i18n_helpers';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { BumpToggleSwitch } from './BumpToggleSwitch';
import { TrendUpIcon } from '@reverbdotcom/cadence/icons/react';

const COMPONENT_NAME = 'BumpModalBodyToggle';

interface Props {
  bid: number;
  handleBidChange: (bid: number) => void;
  listing: CoreBumpRecommendationListingCard.Fragment;
  recommendationAmount: number;
  selectedBid: number;
  successMessage: string;
  failMessage: string;
  hideSubmit?: boolean;
  onSubmit: () => void;
  setShowCustomWarning: React.Dispatch<React.SetStateAction<boolean>>;
  showCustomWarning: boolean;
}

const t = withPrefix('discovery.bumpModal');

const WHAT_IS_BUMP_HELP_CENTER_ARTICLE_ID = '360007185854';

export function BumpModalBodyToggle(props: Props) {
  const mParticleViewData = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ViewedBumpSlider,
    listing: props.listing,
    startingBumpRatePercent: formatBid(props.bid),
    bumpRecommendationPercent: props.recommendationAmount,
  };

  useViewTracking(mParticleViewData, true);

  function Messages() {
    return (
      <>
        {
          props.failMessage &&
          <div className="scaling-mb-4">
            <RCAlertBox type="error">
              <SanitizedRender html={props.failMessage} />
            </RCAlertBox>
          </div>
        }
        {
          props.successMessage &&
          <div className="scaling-mb-4">
            <RCAlertBox type="success">
              {props.successMessage}
            </RCAlertBox>
          </div>
        }
      </>
    );
  }

  return (
    <>
      <Messages />
      <div className="modal-body__header" >
        <div className="modal-body__icon">
          <RCIcon size="rc-space-8" svgComponent={TrendUpIcon} />
        </div>
        <div>
          <div className="scaling-mt-3">
            <Translate className="text-title-600" text="discovery.bumpModal.giveItABump" />
          </div>
          <div className="scaling-mb-2">
            <Translate className="text-utility-400 text-utility--medium" text="discovery.bumpModal.promoteYourListingSubtitle" />
          </div>
        </div>
      </div>
      <div className="scaling-mb-5">
        <Translate html className="text-default-350" text="discovery.bumpModal.description" >
          <CoreLink target="_blank" to={useHelpCenterArticleUrl(WHAT_IS_BUMP_HELP_CENTER_ARTICLE_ID)} >
            <Translate className="text-link" text="discovery.bumpModal.descriptionLink" />
          </CoreLink>
        </Translate>
      </div>
      <div className="scaling-mt-6">
        <BumpToggleSwitch
          handleBidChange={props.handleBidChange}
          recommendationAmount={props.recommendationAmount}
          selectedBid={props.selectedBid}
          showCustomWarning={props.showCustomWarning}
          setShowCustomWarning={props.setShowCustomWarning}
        />
      </div>
      <div className="scaling-mt-5 color-secondary text-fine-300">
        {t('footnote')}
      </div>
      {!props.hideSubmit && (
        <div className="align-right scaling-mt-8">
          <button
            type="button"
            className="button button--primary size-90 weight-bold"
            disabled={props.showCustomWarning}
            onClick={props.onSubmit}
          >
            {t('save')}
          </button>
        </div>
      )}
    </>
  );
}
